/**
 * Represents the value used to indicate a spread of pages when formatting initial and last elements.
 */
export const SPREAD_PAGE_VALUE = -1;

interface IFormatInitialAndLastElements {
  pages: number[];
  currentPage: number;
  currentPageIndex: number;
  sidePages: number;
  totalPages: number;
}

/**
 * Formats an array of page numbers for pagination, adding spread indicators ('...')
 * for ellipsis when there are many pages, focusing on the current page.
 *
 * @param pages - An array of page numbers.
 * @param currentPage - The current active page.
 * @param currentPageIndex - The index of the current active page in the 'pages' array.
 * @param sidePages - The number of pages to be shown on each side of the current page.
 * @param totalPages - The total number of pages.
 *
 * @returns A formatted array of page numbers with spread indicators.
 */
const formatInitialAndLastElements = ({
  pages,
  currentPage,
  currentPageIndex,
  sidePages,
  totalPages,
}: IFormatInitialAndLastElements): number[] => {
  // Make a copy of the input array to avoid modifying the original array.
  let pagesResponse = [...pages];

  // Calculate the total number of side pages to be displayed on each side of the current page.
  const totalSidePages = sidePages * 2;

  // Add spread indicator at the beginning if there are more pages to show on the left side.
  if (currentPageIndex > sidePages && totalPages - totalSidePages > 1) {
    pagesResponse = [1, SPREAD_PAGE_VALUE, ...pagesResponse.slice(2)];
  }

  // Add spread indicator at the end if there are more pages to show on the right side.
  if (currentPage < totalPages - sidePages && totalPages - totalSidePages > 1) {
    pagesResponse = [
      ...pagesResponse.slice(0, -2),
      SPREAD_PAGE_VALUE,
      totalPages,
    ];
  }

  // Return the formatted array of page numbers.
  return pagesResponse;
};

/**
 * Retrieves the available page numbers to be displayed based on the current page and total pages.
 *
 * @param sidePages - Number of pages to be displayed on each side of the current page.
 * @param currentPage - The current active page.
 * @param totalPages - Total number of pages.
 * @returns An array representing the available pages for display.
 */
export const getAvailablePages = (
  sidePages: number,
  currentPage: number,
  totalPages: number,
): number[] => {
  if (totalPages === 1) {
    return [currentPage];
  }

  const totalSidePages = sidePages * 2;
  // Create and populate the initial array of possible values, it will
  // contain 4 times the sidePages value, because there might cases where
  // we have only values from one side (Example: page 1 or last page)
  let pages = Array(sidePages * 4 + 1)
    .fill(0)
    .map((_, index) => {
      return Math.max(0, currentPage - totalSidePages + index);
    })
    .filter((page) => page && page <= totalPages);

  if (totalPages <= totalSidePages + 1) {
    return pages;
  }

  const currentPageIndex = pages.findIndex((page) => page === currentPage);

  // Depending on the position of the current page, we slice the correct value
  if (currentPageIndex <= sidePages) {
    // When we have less values to the left, we want to get the array from the beginning,
    // but with a maximum value of totalSidePages + 1
    pages = pages.slice(0, totalSidePages + 1);
  } else if (pages.length - 1 - currentPageIndex <= sidePages) {
    // When we have less values to the right, we want to get the array from
    // pages.length - totalSidePages - 1 till the end of the array
    pages = pages.slice(pages.length - totalSidePages - 1);
  } else if (pages.length - 1 - currentPageIndex !== 0) {
    // If we are not at the end, then the last case is that we are in the middle
    // for this case we want sidePages to the left and sidePages to the right
    pages = pages.slice(
      currentPageIndex - sidePages,
      currentPageIndex + sidePages + 1,
    );
  }

  return formatInitialAndLastElements({
    pages,
    currentPage,
    currentPageIndex,
    sidePages,
    totalPages,
  });
};
